'use client';

import {
  Check,
  CheckCircle2,
  PenLine,
  Megaphone,
  CalendarCheck,
  Handshake,
  MessageSquare,
} from 'lucide-react';

/* ── FormulaNode ── */

export function FormulaNode({
  icon: Icon,
  title,
  subtitle,
  accentColor = 'gray',
  onClick,
  badge,
  active,
  disabled,
  variant = 'default',
  checkmark,
  subtitleCheck,
  emptyPlaceholder,
  showPen = true,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  subtitle?: string;
  accentColor?: 'gray' | 'blue' | 'purple';
  onClick?: () => void;
  badge?: string | number;
  active?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'cta' | 'cta-active';
  checkmark?: boolean;
  subtitleCheck?: boolean;
  emptyPlaceholder?: boolean;
  showPen?: boolean;
}) {
  const borderColors = {
    gray: 'border-l-base-content/20',
    blue: 'border-l-base-content/30',
    purple: 'border-l-primary',
  };
  const iconColors = {
    gray: 'text-base-content/50',
    blue: 'text-base-content/60',
    purple: 'text-primary',
  };

  const isButton = !!onClick;
  const Tag = isButton ? 'button' : 'div';

  if (variant === 'cta') {
    return (
      <Tag
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className={`h-[68px] w-[220px] rounded-xl px-4 py-3 border-l-4 border-l-primary bg-gradient-to-r from-primary to-purple-500 text-white shadow-md transition-all relative text-left flex items-center ${
          disabled
            ? 'opacity-60 cursor-not-allowed'
            : 'hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] cursor-pointer'
        }`}
      >
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-white" />
          <div className="min-w-0 flex-1 text-left">
            <p className="text-sm font-semibold truncate">{title}</p>
          </div>
        </div>
      </Tag>
    );
  }

  if (variant === 'cta-active') {
    return (
      <Tag
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className="h-[68px] w-[220px] glass-card rounded-xl px-4 py-3 border-l-4 border-l-base-content/20 shadow-md transition-all relative text-left ring-2 ring-primary/30 cursor-pointer flex items-center"
      >
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-base-content/60" />
          <div className="min-w-0 flex-1 text-left">
            <p className="text-sm font-semibold truncate">{title}</p>
          </div>
        </div>
      </Tag>
    );
  }

  // n8n-style empty placeholder variant
  if (emptyPlaceholder) {
    return (
      <Tag
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className={`h-[68px] w-[220px] rounded-xl px-4 py-3 border-2 border-dashed border-base-content/20 transition-all relative flex items-center ${
          isButton && !disabled ? 'hover:border-primary/40 hover:shadow-sm cursor-pointer' : ''
        } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
      >
        <div className="flex items-center gap-2 w-full overflow-hidden">
          <div className="w-7 h-7 rounded-full border-2 border-dashed border-primary/40 flex items-center justify-center shrink-0">
            <span className="text-primary text-lg leading-none">+</span>
          </div>
          <div className="min-w-0 flex-1 text-left overflow-hidden">
            <p className="text-sm font-semibold text-base-content/40 truncate">{title}</p>
            {subtitle && <p className="text-xs text-base-content/30 truncate">{subtitle}</p>}
          </div>
        </div>
      </Tag>
    );
  }

  return (
    <Tag
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`h-[68px] w-[220px] glass-card rounded-xl px-4 py-3 border-l-4 shadow-sm transition-all relative flex items-center group ${
        borderColors[accentColor]
      } ${
        active ? 'ring-2 ring-primary/30 shadow-md' : ''
      } ${
        isButton && !disabled ? 'hover:shadow-md cursor-pointer' : ''
      } ${
        disabled ? 'opacity-40 cursor-not-allowed' : ''
      }`}
    >
      {isButton && !disabled && showPen && (
        <PenLine size={10} className="absolute top-2 right-2 text-base-content/40 transition-colors group-hover:text-base-content/70" />
      )}
      <div className="flex items-center gap-2 w-full overflow-hidden">
        {checkmark ? (
          <CheckCircle2 size={18} className="text-green-500 shrink-0" />
        ) : (
          <Icon size={18} className={`${iconColors[accentColor]} shrink-0`} />
        )}
        <div className="min-w-0 flex-1 text-left overflow-hidden">
          <p className="text-sm font-semibold truncate">{title}</p>
          {subtitle && (
            <p className="text-xs text-base-content/40 truncate flex items-center gap-1">
              {subtitleCheck && <Check size={12} className="text-purple-400 shrink-0" strokeWidth={3} />}
              {subtitle}
            </p>
          )}
        </div>
        {badge !== undefined && (
          <span className="badge badge-sm badge-ghost shrink-0">{badge}</span>
        )}
      </div>
    </Tag>
  );
}

/* ── FormulaOperator ── */

export function FormulaOperator({ symbol }: { symbol: '+' | '=' }) {
  return (
    <div className="flex flex-col items-center shrink-0">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-transparent mb-2 select-none">&middot;</span>
      <span className="h-[68px] flex items-center text-2xl font-bold text-base-content/20 px-4 select-none">{symbol}</span>
    </div>
  );
}

/* ── Goal Templates ── */

export function getGoalTemplates(product: string) {
  const p = product.trim() || 'your product';
  return [
    { id: 'introduce', icon: Megaphone, label: 'Introduce', desc: `Introduce ${p} to potential customers` },
    { id: 'demo', icon: CalendarCheck, label: 'Book demos', desc: `Book a demo call for ${p}` },
    { id: 'partner', icon: Handshake, label: 'Partnerships', desc: `Explore partnerships for ${p}` },
    { id: 'feedback', icon: MessageSquare, label: 'Get feedback', desc: `Get feedback on ${p} from leaders` },
  ];
}
